import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { client } from "@/hera-api/client/client.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { downloadFileFromResponse, getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Award, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";

interface RegistrationCertificateDownloadButtonProps {
  licenseId: string;
  variant?: "outline" | "default" | "secondary" | "ghost";
  size?: "default" | "sm" | "lg";
  className?: string;
}

export function RegistrationCertificateDownloadButton({
  licenseId,
  variant = "outline",
  size = "sm",
  className,
}: RegistrationCertificateDownloadButtonProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const [downloading, setDownloading] = useState(false);

  const { data: templateGlobal, isLoading: templateLoading } = useQuery({
    queryKey: ["registrationCertificateTemplateAvailable", licenseId],
    queryFn: async () => {
      const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
      const auth = client.getConfig().auth;
      const url = `${baseUrl}/shared/licenses/${licenseId}/registration-certificate-template-available`;
      const doFetch =
        client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
      const res = await doFetch(
        new Request(url, {
          headers:
            typeof auth === "string" && auth
              ? { Authorization: `Bearer ${auth}` }
              : {},
        }),
      );
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as { available: boolean };
    },
    enabled: Boolean(licenseId) && isAuthSetupComplete,
  });

  const hasTemplate = templateGlobal?.available ?? false;

  const handleDownload = useCallback(async () => {
    if (!licenseId) return;
    setDownloading(true);
    try {
      const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
      const auth = client.getConfig().auth;
      const url = `${baseUrl}/shared/licenses/${licenseId}/registration-certificate`;
      const doFetch =
        client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
      const res = await doFetch(
        new Request(url, {
          headers:
            typeof auth === "string" && auth
              ? { Authorization: `Bearer ${auth}` }
              : {},
        }),
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      downloadFileFromResponse(
        blob,
        res.headers,
        `registration-certificate-${licenseId}.pdf`,
      );
    } catch (e) {
      toast({
        title: "Could not download registration certificate",
        description: getQueryErrorMessage(e),
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  }, [licenseId]);

  const disabled =
    !hasTemplate || templateLoading || downloading || !isAuthSetupComplete;

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      disabled={disabled}
      title={
        !hasTemplate && !templateLoading
          ? "No registration certificate template is configured for this license type, or Drive output folder is missing."
          : undefined
      }
      onClick={() => void handleDownload()}
    >
      {downloading || templateLoading ? (
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
      ) : (
        <Award className="h-4 w-4 shrink-0" />
      )}
      <span className="ml-1.5">
        {downloading ? "Generating certificate…" : "Registration certificate"}
      </span>
    </Button>
  );
}
